import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { MessageThread } from "@/components/inbox/MessageThread";
import { MessageInput } from "@/components/inbox/MessageInput";
import { Button } from "@/components/ui/button";
import { ChevronLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ListLoadingState } from "@/components/loading/ListLoadingState";
import { useRealtimeMessages } from "@/hooks/useRealtimeMessages";

const Conversation = () => {
  const params = useParams<{ conversationId: string }>();
  const conversationId = params.conversationId;
  const navigate = useNavigate();
  const [newMessage, setNewMessage] = useState("");
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const threadEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchUserId = async () => {
      try {
        const { data: { user }, error } = await supabase.auth.getUser();
        if (error) {
          console.error('Auth error:', error);
          return;
        }
        setCurrentUserId(user?.id || null);
      } catch (error) {
        console.error('Error in fetchUserId:', error);
      }
    };
    fetchUserId();
  }, []);

  const { markConversationAsRead } = useRealtimeMessages(conversationId || null, currentUserId, false);

  const { data: conversation, isLoading: isLoadingConversation, error: conversationError } = useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: async () => {
      if (!conversationId || !currentUserId) {
        return null;
      }

      try {
        const { data, error } = await supabase
          .from('conversations')
          .select(`
            id,
            cook_id,
            foodie_id,
            unread_count,
            cook:profiles!conversations_cook_id_fkey(
              id,
              first_name,
              last_name,
              image_url
            ),
            foodie:profiles!conversations_foodie_id_fkey(
              id,
              first_name,
              last_name,
              image_url
            ),
            meal:meals(
              id,
              name,
              image
            )
          `)
          .eq('id', conversationId)
          .maybeSingle();

        if (error) {
          throw error;
        }

        console.log('Conversation data:', data);
        console.log('Unread count:', data?.unread_count);

        if (data && data.unread_count > 0) {
          markConversationAsRead();
        }

        return data;
      } catch (error) {
        console.error('Error in conversation query:', error);
        throw error;
      }
    },
    enabled: Boolean(conversationId && currentUserId),
    retry: 1,
    refetchInterval: 5000
  });

  const { data: messages = [], isLoading: isLoadingMessages } = useQuery({
    queryKey: ['messages', conversationId],
    queryFn: async () => {
      if (!conversationId || !currentUserId) {
        return [];
      }

      if (!conversation) {
        return [];
      }

      const { data: messagesData, error: messagesError } = await supabase
        .from('messages')
        .select(`
          id,
          content,
          created_at,
          sender_id,
          sender:profiles!messages_sender_id_fkey (
            id,
            first_name,
            last_name,
            image_url
          )
        `)
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (messagesError) {
        throw messagesError;
      }

      console.log("Messages loaded:", messagesData?.length || 0);
      return messagesData || [];
    },
    enabled: !!conversationId && !!currentUserId && !!conversation,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchInterval: false,
    gcTime: 0
  });

  useEffect(() => {
    if (threadEndRef.current) {
      console.log("Scrolling to latest message");
      threadEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey && document.activeElement?.tagName !== 'INPUT') {
        if (newMessage.trim() && !isSending) {
          e.preventDefault();
          sendMessage();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [newMessage, isSending]);

  const sendMessage = async () => {
    if (!conversationId || !newMessage.trim() || !currentUserId || !conversation || isSending) return;

    setIsSending(true);
    const messageContent = newMessage.trim();
    setNewMessage(""); // Clear input immediately for better UX

    try {
      console.log("Sending message as user ID:", currentUserId);
      
      const { error: messageError } = await supabase.from("messages").insert({
        conversation_id: conversationId,
        content: messageContent,
        sender_id: currentUserId,
      });

      if (messageError) throw messageError;

      const isUserCook = currentUserId === conversation.cook_id;
      
      const { error: updateError } = await supabase
        .from('conversations')
        .update({ 
          updated_at: new Date().toISOString()
        })
        .eq('id', conversationId);

      if (updateError) throw updateError;
      
      console.log("Updated conversation timestamp. Unread counts will be handled by the database trigger.");
      
      await queryClient.invalidateQueries({ queryKey: ['messages', conversationId] });
      await queryClient.invalidateQueries({ queryKey: ['conversations'] });
      await queryClient.invalidateQueries({ queryKey: ['unread-messages', false] });
      await queryClient.invalidateQueries({ queryKey: ['cook-conversations'] });
      
      console.log("Message sent successfully");
    } catch (error) {
      console.error('Error sending message:', error);
      setNewMessage(messageContent); // Restore message content on error
      toast({
        title: "Failed to send message",
        variant: "destructive",
        duration: 3000
      });
    } finally {
      setIsSending(false);
    }
  };

  const otherUser = conversation?.cook?.id === currentUserId ? conversation?.foodie : conversation?.cook;

  if (!conversationId) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <p className="text-muted-foreground">Invalid conversation URL</p>
        <Button 
          variant="outline" 
          onClick={() => navigate('/inbox')}
        >
          Return to Inbox
        </Button>
      </div>
    );
  }

  if (isLoadingConversation) {
    return <ListLoadingState />;
  }

  if (conversationError) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <p className="text-muted-foreground">This conversation doesn't exist or you don't have permission to view it.</p>
        <Button 
          variant="outline" 
          onClick={() => navigate('/inbox')}
        >
          Return to Inbox
        </Button>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <p className="text-muted-foreground">Conversation not found</p>
        <Button 
          variant="outline" 
          onClick={() => navigate('/inbox')}
        >
          Return to Inbox
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-background">
      <header className="sticky top-0 z-10 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-14 items-center">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/inbox')}
            className="mr-2"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex flex-col">
            <h2 className="text-sm font-medium">
              {conversation?.meal?.name}
            </h2>
            <p className="text-xs text-muted-foreground">
              with {otherUser?.first_name} {otherUser?.last_name}
            </p>
          </div>
        </div>
      </header>
      <div className="flex-1 overflow-hidden">
        <MessageThread 
          messages={messages} 
          currentUserId={currentUserId}
          isLoading={isLoadingMessages}
          ref={threadEndRef}
        />
        <MessageInput
          value={newMessage}
          onChange={setNewMessage}
          onSend={sendMessage}
          isDisabled={isSending}
        />
      </div>
    </div>
  );
};

export default Conversation;
